/**
 * Smart navigation utility for notifications
 * Uses jsonRecord to determine the best navigation path based on notification type
 */

/**
 * Navigate to the appropriate page based on notification data
 * @param {Object} notification - The notification object
 * @param {Function} navigate - React Router navigate function
 * @returns {boolean} - Returns true if navigation was handled, false otherwise
 */
export const handleNotificationNavigation = (notification, navigate) => {
  if (!notification) return false;

  // Parse jsonRecord if it's a string
  let jsonRecord = notification.jsonRecord;
  if (typeof jsonRecord === 'string') {
    try {
      jsonRecord = JSON.parse(jsonRecord);
    } catch (e) {
      console.warn('Failed to parse jsonRecord:', e);
      jsonRecord = null;
    }
  }

  // If jsonRecord exists, use it for smart navigation
  if (jsonRecord) {
    const notificationType = jsonRecord.notificationType;

    // Handle geofence violation notifications
    if (notificationType === 'geofence_violation' && jsonRecord.site && jsonRecord.breadcrumbId) {
      const siteId = jsonRecord.site.id;
      const customerId = jsonRecord.site.customer?.id;
      
      // Navigate to site page with breadcrumbs tab
      // Format: /customers/:id/:actionType/:recordId
      // The breadcrumbs component will read breadcrumbId from sessionStorage
      if (customerId && siteId) {
        // Store breadcrumbId in sessionStorage for the breadcrumbs component to use
        sessionStorage.setItem('selectedBreadcrumbId', jsonRecord.breadcrumbId);
        // Navigate to site page - the actionType can be 'sites' or similar
        // The breadcrumbs tab is at index 10, but we'll navigate to the site page
        // and the breadcrumbs component will handle the selection
        const path = `/customers/${customerId}/sites/${siteId}`;
        navigate(path);
        return true;
      }
    }

    // Handle schedule notifications
    if (notificationType === 'schedule') {
      const siteId = jsonRecord.site?.id;
      const customerId = jsonRecord.site?.customer?.id;
      const scheduleId = jsonRecord.schedule?.id || jsonRecord.id;
      const scheduleDate = jsonRecord.schedule?.date || jsonRecord.date;
      const startTime = jsonRecord.schedule?.timeStart || jsonRecord.timeStart || jsonRecord.startTime;
      const endTime = jsonRecord.schedule?.timeEnd || jsonRecord.timeEnd || jsonRecord.endTime;
      const totalHours = jsonRecord.schedule?.totalHours || jsonRecord.totalHours;
      
      // Build query parameters for smart navigation
      const queryParams = new URLSearchParams();
      
      if (siteId) {
        queryParams.set('siteId', siteId);
      }
      
      // If we have a schedule date, use it to filter the calendar view
      if (scheduleDate) {
        queryParams.set('date', scheduleDate);
      }
      
      // If we have schedule ID, store it for potential direct navigation
      if (scheduleId) {
        sessionStorage.setItem('selectedScheduleId', scheduleId);
        queryParams.set('scheduleId', scheduleId);
      }
      
      // Store schedule details in sessionStorage for context
      if (startTime || endTime || totalHours) {
        const scheduleDetails = {
          startTime,
          endTime,
          totalHours,
          date: scheduleDate
        };
        sessionStorage.setItem('scheduleNotificationDetails', JSON.stringify(scheduleDetails));
      }
      
      // Navigate to schedules page with filters
      const path = `/schedules${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      navigate(path);
      return true;
    }

    // Handle user-related notifications
    if (notificationType === 'user' && jsonRecord.user) {
      const userId = jsonRecord.user.id;
      if (userId) {
        navigate(`/employees/${userId}`);
        return true;
      }
    }

    // Handle site-related notifications (generic)
    if (jsonRecord.site && !notificationType) {
      const siteId = jsonRecord.site.id;
      const customerId = jsonRecord.site.customer?.id;
      
      if (customerId && siteId) {
        navigate(`/customers/${customerId}/sites/${siteId}`);
        return true;
      }
    }
  }

  // Fallback to relatedEntityUrl if jsonRecord navigation didn't work
  if (notification.relatedEntityUrl) {
    const url = notification.relatedEntityUrl;
    const path = url.startsWith('http') ? new URL(url).pathname : url;
    navigate(path);
    return true;
  }

  return false;
};

/**
 * Get navigation path for a notification (without actually navigating)
 * @param {Object} notification - The notification object
 * @returns {string|null} - The navigation path or null if no path can be determined
 */
export const getNotificationPath = (notification) => {
  if (!notification) return null;

  // Parse jsonRecord if it's a string
  let jsonRecord = notification.jsonRecord;
  if (typeof jsonRecord === 'string') {
    try {
      jsonRecord = JSON.parse(jsonRecord);
    } catch (e) {
      console.warn('Failed to parse jsonRecord:', e);
      jsonRecord = null;
    }
  }

  // If jsonRecord exists, use it for smart navigation
  if (jsonRecord) {
    const notificationType = jsonRecord.notificationType;

    // Handle geofence violation notifications
    if (notificationType === 'geofence_violation' && jsonRecord.site && jsonRecord.breadcrumbId) {
      const siteId = jsonRecord.site.id;
      const customerId = jsonRecord.site.customer?.id;
      
      if (customerId && siteId) {
        return `/customers/${customerId}/breadcrumbs/${siteId}`;
      }
    }

    // Handle schedule notifications
    if (notificationType === 'schedule') {
      const siteId = jsonRecord.site?.id;
      const scheduleId = jsonRecord.schedule?.id || jsonRecord.id;
      const scheduleDate = jsonRecord.schedule?.date || jsonRecord.date;
      
      const queryParams = new URLSearchParams();
      
      if (siteId) {
        queryParams.set('siteId', siteId);
      }
      
      if (scheduleDate) {
        queryParams.set('date', scheduleDate);
      }
      
      if (scheduleId) {
        queryParams.set('scheduleId', scheduleId);
      }
      
      return `/schedules${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    }

    // Handle user-related notifications
    if (notificationType === 'user' && jsonRecord.user) {
      const userId = jsonRecord.user.id;
      if (userId) {
        return `/employees/${userId}`;
      }
    }

    // Handle site-related notifications (generic)
    if (jsonRecord.site && !notificationType) {
      const siteId = jsonRecord.site.id;
      const customerId = jsonRecord.site.customer?.id;
      
      if (customerId && siteId) {
        return `/customers/${customerId}/sites/${siteId}`;
      }
    }
  }

  // Fallback to relatedEntityUrl
  if (notification.relatedEntityUrl) {
    const url = notification.relatedEntityUrl;
    return url.startsWith('http') ? new URL(url).pathname : url;
  }

  return null;
};

